import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import taskApi from '../services/taskApi';
import userApi from '../services/userApi';
import useLanguage from '../hooks/useLanguage';
import TaskPhotoGrid from './TaskPhotoGrid';
import CleaningResultsSummary from './CleaningResultsSummary';
import GuestCheckoutLine from './GuestCheckoutLine';
import GuestAssessmentQuestion, {
  EMPTY_GUEST_ASSESSMENT,
  guestAssessmentProblem,
  guestAssessmentPayload,
  guestAssessmentProblemKey
} from './GuestAssessmentQuestion';
import { taskRefusalKey } from '../utils/taskErrorCodes';
import GuestAssessmentSummary from './GuestAssessmentSummary';
import { formatDateTime, minutesBetween } from '../utils/dateFormatter';
import CleanRecordCorrection from './CleanRecordCorrection';
import { AuthContext } from '../context/AuthContext';
import { TASK_MANAGER_ROLES } from '../config/roleConfig';

const EditTaskModal = ({ show, onHide, task, onTaskUpdated }) => {
  const { t, isArabic, language } = useLanguage();
  const { user } = useContext(AuthContext);
  // A supervisor may correct a clean's start and team (tasks/cleaning-performance-report.md D7) —
  // task managers only, and only on a clean that is under way or finished.
  const canCorrectCleanRecord = TASK_MANAGER_ROLES.includes(user?.role)
    && task?.type === 'cleaning' && ['in-progress', 'completed'].includes(task?.status);

  const PRIORITY_OPTIONS = [
    { value: 'critical', label: t('tasks.priority.critical') },
    { value: 'urgent', label: t('tasks.priority.urgent') },
    { value: 'high', label: t('tasks.priority.high') },
    { value: 'medium', label: t('tasks.priority.medium') },
    { value: 'low', label: t('tasks.priority.low') }
  ];

  const STATUS_OPTIONS = [
    { value: 'pending', label: t('tasks.status.pending') },
    { value: 'assigned', label: t('tasks.status.assigned') },
    { value: 'in-progress', label: t('tasks.status.inProgress') },
    { value: 'completed', label: t('tasks.status.completed') },
    { value: 'cancelled', label: t('tasks.status.cancelAction'), variant: 'danger' }
  ];

  const [formData, setFormData] = useState({
    title: '',
    scheduledDate: '',
    scheduledTime: '',
    priority: 'medium',
    status: 'pending',
    notes: '',
    guestUpdate: '',
    assignedTo: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maintenanceUsers, setMaintenanceUsers] = useState([]);
  // The guest question, when this modal is what closes a turnover clean (L7)
  const [guestAnswer, setGuestAnswer] = useState(EMPTY_GUEST_ASSESSMENT);
  const [photos, setPhotos] = useState([]);
  // A start corrected in «سجل النظافة» while the dialog is open — the «استغرقت» line reads it.
  const [correctedStartedAt, setCorrectedStartedAt] = useState(null);
  useEffect(() => { setCorrectedStartedAt(null); }, [task?._id, show]);
  const effectiveStartedAt = correctedStartedAt || task?.startedAt;

  // Load maintenance users
  useEffect(() => {
    loadMaintenanceUsers();
  }, []);

  const loadMaintenanceUsers = async () => {
    try {
      const response = await userApi.getStaffUsers();
      setMaintenanceUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load maintenance users:', error);
      setMaintenanceUsers([]);
    }
  };

  // Initialize form with task data
  useEffect(() => {
    if (task && show) {
      setFormData({
        title: task.title || '',
        scheduledDate: task.scheduledDate ? new Date(task.scheduledDate).toISOString().split('T')[0] : '',
        scheduledTime: task.scheduledTime || '',
        priority: task.priority || 'medium',
        status: task.status || 'pending',
        notes: task.notes || '',
        guestUpdate: task.guestUpdate || '',
        assignedTo: task.assignedTo?._id || ''
      });
      setError(null);
      setGuestAnswer(EMPTY_GUEST_ASSESSMENT);
      setPhotos(task.photos || []);
    }
  }, [task, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Closing a turnover clean from here asks the same question as every other close path (L7).
  const asksGuestQuestion = !!task?.guestAssessmentPrompt
    && formData.status === 'completed' && task.status !== 'completed';

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Nothing is saved while the answer is incomplete: this modal saves the details before the
    // status, so a refusal after the first save would leave a half-applied edit.
    if (asksGuestQuestion) {
      const problem = guestAssessmentProblem(guestAnswer, task.guestAssessmentPrompt, photos);
      if (problem) {
        setError(t(guestAssessmentProblemKey(problem)));
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare update data
      const updateData = {
        title: formData.title,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime || null,
        priority: formData.priority,
        notes: formData.notes || null,
        guestUpdate: formData.guestUpdate || null
      };

      // Update task via API. Status + assignment are separate endpoints that each
      // return the fully-updated task — keep the latest so the parent (card view)
      // reflects the new status/assignee immediately, not the pre-change snapshot.
      const response = await taskApi.updateTask(task._id, updateData);
      let latestTask = response.data.task;

      // Handle status change separately if changed
      if (formData.status !== task.status) {
        const statusResponse = await taskApi.updateTaskStatus(task._id, formData.status, {
          guestAssessment: asksGuestQuestion ? guestAssessmentPayload(guestAnswer) : undefined
        });
        if (statusResponse?.data?.task) latestTask = statusResponse.data.task;
      }

      // Handle assignment separately if changed
      if (formData.assignedTo !== (task.assignedTo?._id || '')) {
        const assignResponse = await taskApi.assignTask(task._id, formData.assignedTo || null);
        if (assignResponse?.data?.task) latestTask = assignResponse.data.task;
      }

      // Notify parent component
      if (onTaskUpdated) {
        onTaskUpdated(latestTask);
      }

      // Close modal
      onHide();
    } catch (err) {
      console.error('Update task error:', err);
      // A known refusal code is translated for the reader; otherwise the server's reason still beats
      // the generic save-failed text — ops otherwise cannot tell what to do next.
      const refusalKey = taskRefusalKey(err?.response?.data);
      setError(refusalKey ? t(refusalKey) : (err?.response?.data?.error || t('tasks.editModal.errorSaving')));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      size="lg"
      dir={isArabic ? 'rtl' : 'ltr'}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('tasks.editModal.title')}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Location Info (Read-only) — property (PMS) or service location */}
          {task?.displayLocationName && (
            <Alert variant="info">
              <strong>{t('tasks.editModal.property')}:</strong> {task.displayLocationName}
              {task.property?.address && <div className="small">{task.property.address}</div>}
              {task?.ticketId && (
                <div className="small text-primary mt-2">
                  <strong>{t('tasks.card.ticketRef')}:</strong> {task.ticketId}
                </div>
              )}
              {task?.booking?.confirmationCode && (
                <div className="small text-info mt-1">
                  <strong>{t('tasks.editModal.bookingRef')}:</strong> {task.booking.confirmationCode}
                </div>
              )}
              {/* Guest checkout — the one thing on this modal that answers "is the room actually
                  free?", as opposed to "when were we allowed to clean it?". Read-only: recording a
                  checkout belongs to the departures card and the booking page (D7/D27). Full form
                  here — the modal has the room for the recorder's name, unlike the two cards. */}
              {task?.guestCheckout && (
                <GuestCheckoutLine checkout={task.guestCheckout} className="small mt-2" />
              )}
            </Alert>
          )}

          {/* Title */}
          <Form.Group className="mb-3">
            <Form.Label>{t('tasks.editModal.taskTitle')} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder={t('tasks.editModal.taskTitlePlaceholder')}
              dir={isArabic ? 'rtl' : 'ltr'}
            />
          </Form.Group>

          <Row>
            {/* Scheduled Date */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('tasks.editModal.scheduledDate')} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  name="scheduledDate"
                  value={formData.scheduledDate}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            {/* Scheduled Time */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('tasks.editModal.scheduledTime')}</Form.Label>
                <Form.Control
                  type="time"
                  name="scheduledTime"
                  value={formData.scheduledTime}
                  onChange={handleChange}
                  placeholder="14:00"
                />
                <Form.Text className="text-muted">
                  {t('tasks.editModal.scheduledTimeHint')}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* Priority */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('tasks.editModal.priority')} <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                  dir={isArabic ? 'rtl' : 'ltr'}
                >
                  {PRIORITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Status */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('tasks.editModal.status')} <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  dir={isArabic ? 'rtl' : 'ltr'}
                  className={formData.status === 'cancelled' ? 'text-danger' : ''}
                >
                  {STATUS_OPTIONS.map(option => (
                    <option
                      key={option.value}
                      value={option.value}
                      className={option.variant === 'danger' ? 'text-danger' : ''}
                    >
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {asksGuestQuestion && (
            <GuestAssessmentQuestion
              taskId={task._id}
              prompt={task.guestAssessmentPrompt}
              value={guestAnswer}
              onChange={(next) => { setGuestAnswer(next); setError(null); }}
              photos={photos}
              onPhotoAdded={(photo) => setPhotos(prev => [...prev, photo])}
              disabled={loading}
            />
          )}

          <Row>
            {/* Assigned To */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('tasks.editModal.assignedTo')}</Form.Label>
                <Form.Select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  dir={isArabic ? 'rtl' : 'ltr'}
                >
                  <option value="">{t('tasks.editModal.unassigned')}</option>
                  {maintenanceUsers.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Notes */}
          <Form.Group className="mb-3">
            <Form.Label>{t('tasks.editModal.notes')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder={t('tasks.editModal.notesPlaceholder')}
              dir={isArabic ? 'rtl' : 'ltr'}
            />
            <Form.Text className="text-muted">
              {t('tasks.editModal.notesHint')}
            </Form.Text>
          </Form.Group>

          {/* Guest Update */}
          <Form.Group className="mb-3">
            <Form.Label>{t('tasks.editModal.guestMessage')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="guestUpdate"
              value={formData.guestUpdate}
              onChange={handleChange}
              maxLength={500}
              placeholder={t('tasks.editModal.guestMessagePlaceholder')}
              dir={isArabic ? 'rtl' : 'ltr'}
            />
            <Form.Text className="text-muted">
              {t('tasks.editModal.guestMessageHint')} ({formData.guestUpdate?.length || 0}/500)
            </Form.Text>
          </Form.Group>

          {/* Checklist + supplies results (read-only) — lets the supervisor
              review what the crew recorded without being able to overwrite it */}
          <CleaningResultsSummary task={task} />

          {/* Completion summary (read-only) — shown for any completed task */}
          {task?.status === 'completed' && (
            <div className="p-3 rounded mb-3" style={{ background: '#e8f5e9', borderRight: isArabic ? '3px solid #4CAF50' : 'none', borderLeft: isArabic ? 'none' : '3px solid #4CAF50' }}>
              <Form.Label className="text-success fw-semibold">{t('tasks.editModal.fieldTeamReport')}</Form.Label>
              <GuestAssessmentSummary assessment={task.guestAssessment} />
              {task.completedAt && (
                <div className="small text-success mb-2" dir={isArabic ? 'rtl' : 'ltr'}>
                  ✓ {t('tasks.card.completedAt')} {formatDateTime(task.completedAt, language)}
                  {task.completedBy && ` ${t('tasks.card.completedBy')} ${task.completedBy}`}
                </div>
              )}
              {/* How long the job actually took. The two stamps were already recorded and shown
                  nowhere a supervisor could reach — the crew's own card is the only screen that
                  printed the start, and the route blocks admin from opening it. */}
              {minutesBetween(effectiveStartedAt, task.completedAt) !== null && (
                <div className="small text-success mb-2" dir={isArabic ? 'rtl' : 'ltr'}>
                  ⏱ {t('tasks.card.startedAt')} {formatDateTime(effectiveStartedAt, language)}
                  {' · '}
                  {t('tasks.card.took')} {minutesBetween(effectiveStartedAt, task.completedAt)} {t('tasks.card.minutes')}
                </div>
              )}
              {task.completionRating && (
                <div className="mb-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <FaStar key={star} style={{ color: star <= task.completionRating ? '#FFC107' : '#dee2e6', fontSize: '1.2rem' }} />
                  ))}
                </div>
              )}
              {task.completionNotes && (
                <div className="small" dir={isArabic ? 'rtl' : 'ltr'}>{task.completionNotes}</div>
              )}
            </div>
          )}

          {/* Uploaded photos (read-only) — shown whenever the field team attached any */}
          {photos.length > 0 && (
            <div className="mb-3" dir={isArabic ? 'rtl' : 'ltr'}>
              <TaskPhotoGrid photos={photos} />
            </div>
          )}

          {/* Start time + who took part, correctable with a reason; its own save, never the form's.
              Last in the dialog (founder, 23 Sep): what happened — the field report and the photos —
              reads first; the correction tool is the occasional act. */}
          {canCorrectCleanRecord && (
            <CleanRecordCorrection
              task={task}
              staffUsers={maintenanceUsers}
              onCorrected={(record) => setCorrectedStartedAt(record.startedAt)}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            {t('tasks.editModal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="ms-1"
                />
                {t('tasks.editModal.saving')}
              </>
            ) : (
              t('tasks.editModal.save')
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditTaskModal;